import { z } from "zod";

export enum ConfigurationType {
	AddressFavorite = 1,
	PickDefaultAddress = 2,
	DropDefaultAddress = 3,
	CommodityDefault = 4,
	QuoteTemplateDefault = 5,
}

/**
 * Schema for a configuration object
 */
export const configSchema = z.object({
	id: z.number(),
	customerId: z.number().nullable(),
	employeeId: z.number().nullable(),
	customerUserId: z.string().nullable().optional(),
	configType: z.nativeEnum(ConfigurationType),
	configValue: z.string(),
});

/**
 * Schema for creating a new configuration (POST /api/configs request body)
 */
export const configCreateRequestSchema = configSchema;

// Inferred types
export type Config = z.infer<typeof configSchema>;

/**
 * Schema for problem details error response (RFC 7807)
 */
export const problemDetailsSchema = z
	.object({
		type: z.string(),
		title: z.string(),
		status: z.number(),
		detail: z.string(),
		instance: z.string().optional(),
	})
	.catchall(z.string()); // Allows additional properties

export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

/**
 * Schema for the response when creating a new configuration (POST /api/configs response)
 * Can be either the created config or a problem details error
 */
export const configCreateResponseSchema = z.union([
	configSchema,
	problemDetailsSchema,
]);

/**
 * Schema for an array of configuration objects
 */
export const configArraySchema = z.array(configSchema);

/**
 * Schema for the response when getting all configurations (GET /api/configs response)
 * Can be either an array of configs or a problem details error
 */
export const configListSchema = z.union([
	configArraySchema,
	problemDetailsSchema,
]);

/**
 * Schema for the response when deleting a configuration (DELETE /api/configs/{id} response)
 * Can be empty (success), a problem details error, or a string
 */
export const configDeleteSchema = z.union([
	z.string(),
	z.null(),
	z.undefined(),
	problemDetailsSchema,
]);

export type ConfigCreateRequest = z.infer<typeof configCreateRequestSchema>;
export type ConfigCreateResponse = z.infer<typeof configCreateResponseSchema>;
export type ConfigListResponse = z.infer<typeof configListSchema>;
export type ConfigDeleteResponse = z.infer<typeof configDeleteSchema>;
